import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, Inbox as InboxIcon, Settings as SettingsIcon } from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";
import PrelaunchVipModal from "@/components/PrelaunchVipModal";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LangSwitcher from "@/components/LangSwitcher";
import { useI18n } from "@/lib/i18n";

const NAV_ITEMS = [
    { to: "/videos", key: "nav.videos", testid: "nav-videos" },
    { to: "/offers", key: "nav.offers", testid: "nav-offers" },
    // Marketplace nav link removed for launch — route still exists for direct access
    { to: "/partners", key: "nav.partners", testid: "nav-partners" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const isAuthed = user && user !== false;
    const isAdmin = isAuthed && user.role === "admin";
    const isDeveloper = isAuthed && (user.role === "developer" || user.role === "admin");

    // Poll inbox unread count every 5s while authenticated
    const [unread, setUnread] = useState(0);
    const [vipModal, setVipModal] = useState({ open: false, source: "" });
    useEffect(() => {
        if (!isAuthed) return;
        let mounted = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/messages/unread-count");
                if (mounted) setUnread(data?.unread || 0);
            } catch { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 5000);
        return () => { mounted = false; clearInterval(id); };
    }, [isAuthed]);

    return (
        <div className="min-h-screen flex flex-col bg-background text-foreground">
            <header
                className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A1128]/95 border-b border-[#D4AF37]/30"
                data-testid="app-header"
                style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 text-white" data-testid="nav-home">
                        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                        <div className="leading-none">
                            <div className="font-heading text-lg font-bold tracking-tight">ADN</div>
                            <div className="eyebrow text-[9px] text-[#D4AF37]">Local Rewards Network</div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV_ITEMS.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={n.testid}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                                        isActive ? "text-[#D4AF37]" : "text-white/85 hover:text-[#D4AF37]"
                                    }`
                                }
                            >
                                {t(n.key)}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-2">
                        <InstallAppButton />
                        <LangSwitcher darkTheme />
                        <button
                            onClick={() => navigate("/settings")}
                            className="text-white hover:text-[#D4AF37] p-2 rounded-sm hover:bg-white/5 transition-colors"
                            data-testid="nav-settings"
                            aria-label="Settings"
                            title="Settings"
                        >
                            <SettingsIcon className="w-5 h-5" />
                        </button>
                        {!isAuthed ? (
                            <>
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate("/login")}
                                    className="text-white hover:text-[#D4AF37] hover:bg-white/5"
                                    data-testid="header-login-btn"
                                >
                                    {t("nav.signin")}
                                </Button>
                                <Button
                                    onClick={() => setVipModal({ open: true, source: "header" })}
                                    className="bg-[#9B1B30] hover:bg-[#B8233C] text-white border-b-2 border-[#D4AF37] rounded-sm"
                                    data-testid="header-join-btn"
                                >
                                    Reserve $5/mo VIP
                                </Button>
                            </>
                        ) : (
                            <>
                                {isAdmin && (
                                    <Button
                                        variant="ghost"
                                        onClick={() => navigate("/admin")}
                                        className="text-[#D4AF37] hover:bg-white/5"
                                        data-testid="nav-admin"
                                    >
                                        {t("nav.admin")}
                                    </Button>
                                )}
                                <button
                                    onClick={() => navigate("/inbox")}
                                    className="relative text-white hover:text-[#D4AF37] p-2 rounded-sm hover:bg-white/5 transition-colors"
                                    data-testid="nav-inbox"
                                    aria-label="Inbox"
                                    title="Inbox"
                                >
                                    <InboxIcon className="w-5 h-5" />
                                    {unread > 0 && (
                                        <span
                                            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#9B1B30] text-white text-[10px] font-bold leading-none border-2 border-[#0A1128]"
                                            data-testid="nav-inbox-badge"
                                        >
                                            {unread > 99 ? "99+" : unread}
                                        </span>
                                    )}
                                </button>
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate("/dashboard")}
                                    className="text-white hover:text-[#D4AF37] hover:bg-white/5"
                                    data-testid="nav-dashboard"
                                >
                                    {t("nav.dashboard")}
                                </Button>
                                {/* Developer dashboard nav link removed for launch — route still accessible via direct URL */}
                                <Button
                                    onClick={async () => { await logout(); navigate("/"); }}
                                    variant="outline"
                                    className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent"
                                    data-testid="header-logout-btn"
                                >
                                    {t("nav.signout")}
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="lg:hidden flex items-center gap-2">
                        <InstallAppButton compact />
                        <button
                            className="text-white p-2"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                        >
                            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="lg:hidden border-t border-white/10 bg-[#0A1128]" data-testid="mobile-menu">
                        <div className="px-4 py-3 space-y-1">
                            {NAV_ITEMS.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setOpen(false)}
                                    className="block px-3 py-2 rounded-sm text-white hover:bg-white/5"
                                    data-testid={`m-${n.testid}`}
                                >
                                    {t(n.key)}
                                </NavLink>
                            ))}
                            <div className="pt-2 flex items-center justify-between gap-2">
                                <NavLink
                                    to="/settings"
                                    onClick={() => setOpen(false)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-sm text-white hover:bg-white/5 flex-1"
                                    data-testid="m-nav-settings"
                                >
                                    <SettingsIcon className="w-4 h-4 text-[#D4AF37]" />
                                    <span className="text-sm">Settings</span>
                                </NavLink>
                                <LangSwitcher darkTheme />
                            </div>
                            <div className="pt-3 mt-3 border-t border-white/10 flex flex-col gap-2">
                                {!isAuthed ? (
                                    <>
                                        <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="w-full border-white/20 bg-transparent text-white" data-testid="m-login-btn">{t("nav.signin")}</Button>
                                        <Button onClick={() => { setOpen(false); setVipModal({ open: true, source: "mobile-menu" }); }} className="w-full bg-[#9B1B30] text-white" data-testid="m-join-btn">Reserve $5/mo VIP</Button>
                                    </>
                                ) : (
                                    <>
                                        {isAdmin && <Button onClick={() => { setOpen(false); navigate("/admin"); }} variant="outline" className="w-full border-[#D4AF37]/50 text-[#D4AF37] bg-transparent" data-testid="m-admin">{t("nav.admin")}</Button>}
                                        {isDeveloper && <Button onClick={() => { setOpen(false); navigate("/developer"); }} variant="outline" className="w-full border-white/20 bg-transparent text-white" data-testid="m-developer">{t("nav.developer")}</Button>}
                                        <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} variant="outline" className="w-full border-white/20 bg-transparent text-white" data-testid="m-dashboard">{t("nav.dashboard")}</Button>
                                        <Button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} className="w-full bg-[#9B1B30] text-white" data-testid="m-logout">{t("nav.signout")}</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            <footer className="bg-[#050814] text-white/80 mt-16 relative" data-testid="app-footer">
                <div className="stripe-divider"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                            <span className="font-heading text-xl text-white">ADN</span>
                        </div>
                        <p className="text-sm text-white/60 leading-relaxed">
                            {t("footer.tagline")}
                        </p>
                    </div>
                    <div>
                        <div className="eyebrow text-[#D4AF37] mb-3">{t("footer.col.platform")}</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/about" data-testid="footer-about-link" className="hover:text-[#D4AF37]">About ADN</Link></li>
                            <li><Link to="/videos" className="hover:text-[#D4AF37]">{t("nav.videos")}</Link></li>
                            <li><Link to="/offers" className="hover:text-[#D4AF37]">{t("nav.offers")}</Link></li>
                            <li><Link to="/partners" className="hover:text-[#D4AF37]">{t("nav.partners")}</Link></li>
                            <li><a href="mailto:hello@axxessdenied.com" data-testid="footer-contact-email" className="hover:text-[#D4AF37]">Contact us</a></li>
                        </ul>
                    </div>
                    <div>
                        <div className="eyebrow text-[#D4AF37] mb-3">{t("footer.col.legal")}</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/terms" className="hover:text-[#D4AF37]">{t("footer.col.terms")}</Link></li>
                            <li><Link to="/privacy" className="hover:text-[#D4AF37]">{t("footer.col.privacy")}</Link></li>
                            <li><Link to="/voucher-terms" className="hover:text-[#D4AF37]">{t("footer.col.voucher_terms")}</Link></li>
                            <li><Link to="/video-terms" className="hover:text-[#D4AF37]">{t("footer.col.video_terms")}</Link></li>
                            <li><Link to="/referral-terms" className="hover:text-[#D4AF37]">{t("footer.col.referral_terms")}</Link></li>
                            <li><Link to="/founding-business-terms" className="hover:text-[#D4AF37]">Founding Business Partner Terms</Link></li>
                            <li><Link to="/ambassador-terms" className="hover:text-[#D4AF37]">Ambassador Program Terms</Link></li>
                            <li><Link to="/youth-terms" className="hover:text-[#D4AF37]">Youth Terms (13–17)</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="eyebrow text-[#D4AF37] mb-3">{t("footer.col.compliance")}</div>
                        <p className="text-xs text-white/70 leading-relaxed">
                            ADN Local Rewards & Loyalty Network™ is a paid membership and rewards platform. This is not an investment, equity, or securities offering. Founding VIP pricing locked to original member only and is non-transferable.
                        </p>
                    </div>
                </div>
                <div className="border-t border-white/10 py-5 text-center text-xs text-white/70 space-y-1">
                    <div>© {new Date().getFullYear()} ADN Local Rewards &amp; Loyalty Network™.</div>
                    <div>{t("footer.copyright")}</div>
                    <div>
                        Developed by{" "}
                        <a
                            href="https://www.techtronicsmedia.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-techtronics-link"
                            className="text-[#D4AF37] hover:text-white underline underline-offset-4 transition-colors"
                        >
                            Techtronics Media
                        </a>
                    </div>
                </div>
            </footer>
            <PrelaunchVipModal
                open={vipModal.open}
                onClose={() => setVipModal({ open: false, source: "" })}
                source={vipModal.source}
            />
        </div>
    );
}
